import { useState, useEffect, useRef, useCallback } from "react";

export default function useSpeechSynthesis() {
  const [voices, setVoices] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [voicesLoading, setVoicesLoading] = useState(true);
  const [defaultVoice, setDefaultVoice] = useState(null);

  const defaultVoiceRef = useRef(null);
  const queueRef = useRef([]);
  const processingRef = useRef(false);
  const mountedRef = useRef(true);
  const speakDelayRef = useRef(null);
  const repeatTimeoutRef = useRef(null);
  const isEnabledRef = useRef(false);
  const enableListenersRef = useRef([]);
  const [isSpeechEnabled, setIsSpeechEnabled] = useState(false);

  const getVoices = useCallback(() => {
    return new Promise((resolve) => {
      if ("speechSynthesis" in window) {
        let availableVoices = window.speechSynthesis.getVoices();

        if (availableVoices.length > 0) {
          resolve(availableVoices);
          return;
        }

        const voicesChangedHandler = () => {
          availableVoices = window.speechSynthesis.getVoices();
          if (availableVoices.length > 0) {
            window.speechSynthesis.removeEventListener(
              "voiceschanged",
              voicesChangedHandler,
            );
            resolve(availableVoices);
          }
        };

        window.speechSynthesis.addEventListener(
          "voiceschanged",
          voicesChangedHandler,
        );

        setTimeout(() => {
          availableVoices = window.speechSynthesis.getVoices();
          window.speechSynthesis.removeEventListener(
            "voiceschanged",
            voicesChangedHandler,
          );
          resolve(availableVoices);
        }, 1000);
      } else {
        resolve([]);
      }
    });
  }, []);

  const findDefaultVoice = useCallback((voicesList) => {
    if (!voicesList || voicesList.length === 0) return null;

    const ziraVoice = voicesList.find(
      (v) =>
        v.name === "Microsoft Zira - English (United States)" ||
        v.name === "Microsoft Zira Desktop - English (United States)" ||
        (v.name.includes("Zira") && v.lang === "en-US"),
    );

    if (ziraVoice) {
      return ziraVoice;
    }

    const preferredVoiceNames = [
      "Google US English Female",
      "Samantha",
      "Victoria",
      "Karen",
      "Moira",
      "Tessa",
      "Fiona",
      "Google UK English Female",
      "Microsoft Linda",
    ];

    for (const preferredName of preferredVoiceNames) {
      const voice = voicesList.find((v) =>
        v.name.toLowerCase().includes(preferredName.toLowerCase()),
      );
      if (voice) {
        return voice;
      }
    }

    const englishFemaleVoice = voicesList.find(
      (v) =>
        v.lang.startsWith("en") &&
        (v.name.toLowerCase().includes("female") ||
          v.name.toLowerCase().includes("woman") ||
          v.name.toLowerCase().includes("samantha") ||
          v.name.toLowerCase().includes("victoria") ||
          v.name.toLowerCase().includes("karen") ||
          v.name.toLowerCase().includes("moira") ||
          v.name.toLowerCase().includes("tessa") ||
          v.name.toLowerCase().includes("fiona") ||
          v.name.toLowerCase().includes("linda")),
    );

    if (englishFemaleVoice) {
      return englishFemaleVoice;
    }

    const anyEnglishVoice = voicesList.find((v) => v.lang.startsWith("en"));
    if (anyEnglishVoice) {
      return anyEnglishVoice;
    }

    return voicesList[0];
  }, []);

  const stopCurrentSpeech = useCallback(() => {
    if (speakDelayRef.current) {
      clearTimeout(speakDelayRef.current);
      speakDelayRef.current = null;
    }
    if (repeatTimeoutRef.current) {
      clearTimeout(repeatTimeoutRef.current);
      repeatTimeoutRef.current = null;
    }
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    setIsPlaying(false);
    setIsPaused(false);
  }, []);

  const cancel = useCallback(() => {
    queueRef.current = [];
    processingRef.current = false;
    stopCurrentSpeech();
  }, [stopCurrentSpeech]);

  const markSpeechEnabled = useCallback(() => {
    if (isEnabledRef.current) return;
    isEnabledRef.current = true;
    setIsSpeechEnabled(true);
    enableListenersRef.current.forEach(({ event, handler }) => {
      document.removeEventListener(event, handler);
    });
    enableListenersRef.current = [];
  }, []);

  const enableSpeechSynthesis = useCallback(() => {
    if (!("speechSynthesis" in window)) {
      return Promise.resolve(false);
    }

    if (isEnabledRef.current) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      let settled = false;
      const finish = (success) => {
        if (settled) return;
        settled = true;
        if (success) {
          markSpeechEnabled();
        }
        resolve(success);
      };

      const timeoutId = setTimeout(() => finish(false), 2000);

      const silentUtterance = new SpeechSynthesisUtterance("");
      silentUtterance.volume = 0;
      silentUtterance.onstart = () => {
        clearTimeout(timeoutId);
        window.speechSynthesis.cancel();
        finish(true);
      };
      silentUtterance.onerror = () => {
        clearTimeout(timeoutId);
        finish(false);
      };

      try {
        window.speechSynthesis.speak(silentUtterance);
      } catch (error) {
        clearTimeout(timeoutId);
        console.warn("Failed to enable speech synthesis:", error);
        finish(false);
      }
    });
  }, [markSpeechEnabled]);

  const createUtterance = useCallback((text, options) => {
    const utterance = new SpeechSynthesisUtterance(text);

    utterance.lang = options.lang || "en-US";
    utterance.rate = options.speech_rate ?? 1;
    utterance.pitch = options.speech_pitch ?? 1;
    utterance.volume =
      options.speech_volume !== undefined ? options.speech_volume : 1;

    if (options.voice_name) {
      utterance.voice = options.voice_name;
    } else if (defaultVoiceRef.current) {
      utterance.voice = defaultVoiceRef.current;
    }

    return utterance;
  }, []);

  const runUtterance = useCallback(
    (text, options, repeatIndex) => {
      return new Promise((resolve, reject) => {
        const utterance = createUtterance(text, options);
        const totalRepeats = options.repetition_count ?? 1;

        utterance.onstart = () => {
          if (!mountedRef.current) return;
          setIsPlaying(true);
          setIsPaused(false);
          if (repeatIndex === 0) {
            options.onStart?.();
          }
        };

        utterance.onend = () => {
          if (!mountedRef.current) return;

          const nextRepeat = repeatIndex + 1;

          if (nextRepeat < totalRepeats) {
            options.onRepeat?.(nextRepeat);
            repeatTimeoutRef.current = setTimeout(() => {
              runUtterance(text, options, nextRepeat)
                .then(resolve)
                .catch(reject);
            }, options.repeatDelay || 500);
          } else {
            setIsPlaying(false);
            setIsPaused(false);
            options.onEnd?.();
            resolve({
              completed: true,
              repeats: nextRepeat,
            });
          }
        };

        utterance.onerror = (event) => {
          if (!mountedRef.current) return;

          if (event.error === "canceled" || event.error === "interrupted") {
            setIsPlaying(false);
            setIsPaused(false);
            resolve({
              completed: false,
              repeats: repeatIndex,
              canceled: true,
            });
            return;
          }

          setIsPlaying(false);
          setIsPaused(false);
          options.onError?.(event);
          reject(new Error(event.error || "Speech synthesis failed"));
        };

        utterance.onpause = () => {
          setIsPaused(true);
          options.onPause?.();
        };

        utterance.onresume = () => {
          setIsPaused(false);
          options.onResume?.();
        };

        utterance.onboundary = (event) => {
          options.onBoundary?.(event);
        };

        window.speechSynthesis.speak(utterance);
      });
    },
    [createUtterance],
  );

  const executeSpeak = useCallback(
    (text, options) => {
      enableSpeechSynthesis();
      stopCurrentSpeech();

      return new Promise((resolve, reject) => {
        speakDelayRef.current = setTimeout(() => {
          speakDelayRef.current = null;

          if (!mountedRef.current) {
            resolve({ completed: false, canceled: true });
            return;
          }

          runUtterance(text, options, 0).then(resolve).catch(reject);
        }, 100);
      });
    },
    [enableSpeechSynthesis, runUtterance, stopCurrentSpeech],
  );

  const processQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    while (queueRef.current.length > 0 && mountedRef.current) {
      const item = queueRef.current.shift();
      try {
        const result = await executeSpeak(item.text, item.options);
        item.resolve(result);
      } catch (error) {
        item.reject(error);
      }
    }

    processingRef.current = false;
  }, [executeSpeak]);

  const speak = useCallback(
    (text, options = {}) => {
      return new Promise((resolve, reject) => {
        if (!("speechSynthesis" in window)) {
          reject(new Error("Speech synthesis not supported in this browser."));
          return;
        }

        queueRef.current.push({ text, options, resolve, reject });
        processQueue();
      });
    },
    [processQueue],
  );

  const pause = useCallback(() => {
    if ("speechSynthesis" in window && window.speechSynthesis.speaking) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  }, []);

  const resume = useCallback(() => {
    if ("speechSynthesis" in window && window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    }
  }, []);

  useEffect(() => {
    defaultVoiceRef.current = defaultVoice;
  }, [defaultVoice]);

  useEffect(() => {
    if ("speechSynthesis" in window && !isEnabledRef.current) {
      enableSpeechSynthesis();

      const events = ["click", "touchstart", "keydown"];
      const handlers = [];

      events.forEach((event) => {
        const handler = () => {
          enableSpeechSynthesis();
        };
        document.addEventListener(event, handler, {
          once: true,
          passive: true,
        });
        handlers.push({ event, handler });
      });

      enableListenersRef.current = handlers;

      return () => {
        handlers.forEach(({ event, handler }) => {
          document.removeEventListener(event, handler);
        });
        enableListenersRef.current = [];
      };
    }
  }, [enableSpeechSynthesis]);

  useEffect(() => {
    const loadVoices = async () => {
      setVoicesLoading(true);
      try {
        const availableVoices = await getVoices();
        setVoices(availableVoices);

        const selectedVoice = findDefaultVoice(availableVoices);
        setDefaultVoice(selectedVoice);
        defaultVoiceRef.current = selectedVoice;
      } catch (error) {
        console.error("Error loading voices:", error);
        setVoices([]);
      } finally {
        setVoicesLoading(false);
      }
    };

    loadVoices();
  }, [getVoices, findDefaultVoice]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cancel();
    };
  }, [cancel]);

  return {
    speak,
    pause,
    resume,
    cancel,
    enableSpeechSynthesis,
    isSpeechEnabled,
    voices,
    defaultVoice,
    isPlaying,
    isPaused,
    voicesLoading,
    getVoices,
  };
}
